<template>
  <!-- однодневное событие ИЛИ мероприятие с одним однодневным событием -->
  <template v-if="isOneDayEvent">
    <template v-if="beginsAt && isThisYear(beginsAt)">
      <UiDate
        v-if="value.beginsAt"
        :value="value.beginsAt"
        :template="isHours ? 'd MMMM HH:mm' : 'd MMMM'"
      />
      <UiDateWeekday
        v-if="value.beginsAt"
        :value="value.beginsAt"
        class="ml-1 !uppercase"
      />
    </template>
    <template v-else>
      <UiDate
        v-if="value.beginsAt"
        :value="value.beginsAt"
        template="d MMMM yyy"
      />
    </template>
  </template>

  <template v-else-if="isMultipleEventsInOneDay">
    <template v-if="beginsAt && isThisYear(beginsAt)">
      <UiDate v-if="value.beginsAt" :value="value.beginsAt" template="d MMMM" />
    </template>
    <template v-else>
      <UiDate
        v-if="value.beginsAt"
        :value="value.beginsAt"
        template="d MMMM yyy"
      />
    </template>
  </template>

  <UiDatePeriod v-else :value="value" :template="datePeriodTemplate" />
</template>

<script lang="ts" setup>
import { isSameDay, isSameMonth, isSameYear, isThisYear } from 'date-fns'
import type { DateRange, DatePeriodTemplate } from '@/models'

const props = defineProps({
  value: {
    type: Object as PropType<DateRange>,
    required: true,
  },
  activeEventsCount: {
    type: Number,
    default: 0,
  },
  isHours: {
    type: Boolean,
    default: true,
  },
  ignoreOneDayEvent: {
    type: Boolean,
    required: false,
    default: false,
  },
  isMultiDay: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const SPACE_SYMBOL = '&nbsp;'
const COMMA_SYMBOL = `,${SPACE_SYMBOL}`
const DASH_SYMBOL = `${SPACE_SYMBOL}&ndash;${SPACE_SYMBOL}`

const beginsAt = computed(() => {
  return (props.value.beginsAt && getUtcDate(props.value.beginsAt)) || ''
})
const endsAt = computed(() => {
  return (props.value.endsAt && getUtcDate(props.value.endsAt)) || ''
})

const isMultipleEventsInOneDay = computed(
  () =>
    beginsAt.value &&
    endsAt.value &&
    props.activeEventsCount > 1 &&
    isSameDay(beginsAt.value, endsAt.value),
)

const isOneDayEvent = computed(
  () =>
    (!props.ignoreOneDayEvent &&
      beginsAt.value &&
      endsAt.value &&
      isSameDay(beginsAt.value, endsAt.value)) ||
    (props.ignoreOneDayEvent &&
      !props.isMultiDay &&
      (!props.activeEventsCount || props.activeEventsCount === 1)),
)

const getTemplate = ({
  start = '',
  end = '',
  delimiter = DASH_SYMBOL,
}): DatePeriodTemplate => {
  return {
    start,
    end,
    delimiter,
  }
}

const datePeriodTemplate = computed(() => {
  const inThisYear = beginsAt.value && isThisYear(beginsAt.value)

  // если начало и конец в разных годах
  if (
    beginsAt.value &&
    endsAt.value &&
    !isSameYear(beginsAt.value, endsAt.value)
  ) {
    return getTemplate({
      start: 'LLLL yyy',
      end: 'LLLL yyy',
    })
  }

  // если начало и конец в одном году но разных месяцах
  if (
    beginsAt.value &&
    endsAt.value &&
    !isSameMonth(beginsAt.value, endsAt.value)
  ) {
    const start = 'LLLL'
    const end = inThisYear ? 'LLLL' : 'LLLL yyy'

    return getTemplate({ start, end })
  }

  // если начало и конец в одном году и в одинаковыхы месяцах

  const start = inThisYear ? 'd' : 'LLLL'
  const end = inThisYear ? 'd MMMM' : 'yyy'

  let delimiter: string

  if (props.activeEventsCount === 2) {
    delimiter = inThisYear ? COMMA_SYMBOL : SPACE_SYMBOL
  } else {
    delimiter = inThisYear ? DASH_SYMBOL : SPACE_SYMBOL
  }

  return getTemplate({
    start,
    end,
    delimiter,
  })
})
</script>
